<!--
  TODO: for claims, file upload persistence is not working due to technical limitation of storing data on session storage (session storage can only store string and there is limitation of converting file into string and convert back to file later)
-->

<template>
  <fieldset id="service-information">
    <ApiLoading v-if="checkSomeObjectPropertiesTrue(apiStatus)" :apiError="apiStatus.fail" class="api-loading" isMemberPortal>
      <p>Sorry, something has gone wrong, and we can’t show you all the information.</p>
      <p>Try refreshing your page.</p>
    </ApiLoading>

    <template v-else>
      <SectionSeparatorWrapper
        v-for="(claimLine, index) in formResponse.claimLines"
        :key="`claim-service-wrapper-${index}-for-${feature.step1.member}`"
        :lastSection="formResponse.claimLines !== undefined && index == formResponse.claimLines.length - 1"
        :separator="false"
        headerText="Enter service information"
        headerRequiredFieldsText="Indicates required field"
      >
        <h3 class="h6 sub-heading-underlined">Service details</h3>

        <div class="form-section">
          <FormField
            labelText="Date of service"
            :overrideValue="feature.step1.serviceDate"
            type="text"
            :disabled="true"
            isDateInput
            maxlength="14"
            v-model.lazy.trim="claimLine.serviceDate"
            :error="v$.formResponse.claimLines.$each.$response?.$errors?.[index]?.serviceDate?.length > 0"
            :errorMessage="dateErrorMessage(v$.formResponse.claimLines.$each.$response?.$errors?.[index]?.serviceDate || [])"
          >
            <template v-if="!isCRM && index == 0 && refData.content.dateOfServiceHelpText" v-slot:helperText>
              <div v-html="refData.content.dateOfServiceHelpText.informationText" />
            </template>
          </FormField>
        </div>
        <div class="form-section">
          <FormField labelText="Service description" :forId="`service-code-dropdown-${index}-for-${feature.step1.member}`">
            <MultiSelect
              :key="`claim-service-${index}-code-for-${feature.step1.member}:${feature.step4.claimLines[index].serviceId}`"
              :id="`service-code-dropdown-${index}`"
              :maximum="1"
              :items="serviceOptions"
              :selectedItems="selectedService(index)"
              @updateData="selectService"
              :dataToEmitWith="index"
              placeholderText="Enter/select the item number or description"
            />
            <label
              v-if="duplicateService && thisClaimLineIsDuplicate(index) && feature.step4.claimLines[index].serviceId !== ''"
              class="error"
              :for="`service-code-dropdown-${index}-for-${feature.step1.member}`"
            >
              We cannot provide a quote for the same service, more than once per day. Please amend the service details and/or date of service.
            </label>
          </FormField>
        </div>
        <div v-if="isToothIdRequired(claimLine.serviceId)" class="form-section">
          <FormField
            labelText="Tooth number"
            type="text"
            placeholder="e.g. 25"
            v-model.lazy.trim="claimLine.toothId"
            :error="v$.formResponse.claimLines.$each.$response?.$errors?.[index]?.toothId?.length > 0"
            errorMessage="This item number can only contain 2 digits and it must be a valid item number."
          />
        </div>
        <div class="form-section">
          <FormField
            labelText="Provider's fee"
            placeholder="e.g. 100.00"
            type="text"
            prefixLabel="$"
            v-model.lazy.trim="claimLine.providerFee"
            @keydown="preventInvalidCurrencyInput"
            :error="v$.formResponse.claimLines.$each.$response?.$errors?.[index]?.providerFee?.length > 0"
            errorMessage="Please enter an amount greater than $1.00."
          />
        </div>
        <div v-if="isClaim" class="form-section">
          <FormField labelText="Document Upload" :required="fileRequired">
            <p>We accept .jpg, .png, .gif and .pdf files up to 32MB. There is no limit on the number of files you can upload, but all files cannot exceed 128 MB.</p>
            <FileSelect
              :key="`service-file-select-${index}-for-${feature.step1.member}-${duplicatedCount}-${removedCount}`"
              :id="`service-file-select-${index}-for-${feature.step1.member}`"
              :uploadedFiles="formResponse.claimLines[index].uploadedFiles"
              uploadAsFileSelect
              @upload="fileAdded"
              :dataToEmitWith="{ index }"
            />
            <template v-if="!isCRM" v-slot:helperText><p>Please provide documents of your service received.</p></template>
          </FormField>
        </div>

        <div class="form-section">
          <button
            class="non-button-style"
            v-if="isClaim && formResponse.claimLines !== undefined && formResponse.claimLines.length < MAX_CLAIMABLE_SERVICE_LINES"
            @click="duplicateThisService(claimLine, index)"
          >
            Duplicate service
          </button>
        </div>

        <div v-if="formResponse.claimLines !== undefined && formResponse.claimLines.length > 1" class="form-section">
          <button class="non-button-style" @click="removeService(index)">Remove service</button>
        </div>
      </SectionSeparatorWrapper>

      <div class="form-section">
        <button
          class="non-button-style"
          v-if="formResponse.claimLines !== undefined && formResponse.claimLines.length < MAX_CLAIMABLE_SERVICE_LINES"
          @click="addAnotherService"
        >
          Add another service
        </button>
      </div>

      <p>A maximum of {{ MAX_CLAIMABLE_SERVICE_LINES }} service{{ MAX_CLAIMABLE_SERVICE_LINES > 1 ? 's' : '' }} can be {{ isClaim ? 'claimed' : 'quoted' }} at one time.</p>

      <div class="form-page-nav">
        <section>
          <button class="secondary" @click="updatePage(currentPage - 1)">Back</button>
          <button @click="handleNextPage(currentPage + 1)" :disabled="v$.$invalid || v$.$error">Next</button>
        </section>
      </div>
    </template>
  </fieldset>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import FileSelect from '@general/js/templates/JoinNow/JoinNowSteps/EligibilityTypes/components/FileSelect.vue';
import { DATE_VALIDATOR } from '@general/js/validators';
import { checkSomeObjectPropertiesTrue } from '@general/js/utils/ObjectUtils';
import { isCurrentOrFutureDate, isDateInRange, isValidProviderFee } from '@/utils';
import GetABenefitQuoteStepsMixin from './GABQnMANC_StepsMixins';

const UPLOADED_FILES_INITIAL_OBJECT = {
  size: 0,
  files: [],
  fileImgs: [],
  fileInvalid: false,
};

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'GABQ-step-4',

  mixins: [GetABenefitQuoteStepsMixin],

  components: {
    FileSelect,
  },

  data() {
    return {
      MAX_CLAIMABLE_SERVICE_LINES: 20,
      formResponse: {},
      duplicateService: false,
      duplicatedCount: 0,
      removedCount: 0,
      fileRequired: false,
    };
  },

  created() {
    const claimLines = this.feature.step4.claimLines;
    claimLines.forEach((claimLine) => {
      if (this.isClaim) {
        claimLine.uploadedFiles = this.cloneDeep(UPLOADED_FILES_INITIAL_OBJECT);
      }
      claimLine.serviceDate = this.feature.step1.serviceDate;
    });
  },

  computed: {
    serviceOptions() {
      return this.refData.services.map((service) => {
        return {
          display: this.getServiceDisplayName(service),
          returnValue: service.serviceId,
          flexCode: service.flexCode,
        };
      });
    },

    validationsObj() {
      let serviceDateRules;
      if (this.isClaim) {
        const today = new Date(new Date().toDateString());
        const twoYearsAgoToday = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
        serviceDateRules = {
          required,
          DATE_VALIDATOR,
          dateInRange(date) {
            return isDateInRange(date, twoYearsAgoToday, today);
          },
        };
      } else {
        serviceDateRules = {
          required,
          isCurrentOrFutureDate,
          DATE_VALIDATOR,
        };
      }

      const obj = {
        duplicateService: {
          noDuplicates: (value) => {
            return value === false;
          },
        },
        formResponse: {
          claimLines: {
            required,
            $each: helpers.forEach({
              serviceDate: serviceDateRules,
              serviceId: {
                required,
              },
              flexCode: {
                required,
              },
              toothId: {
                isValidToothId(toothId, claimLine) {
                  return this.validToothNumber(toothId, claimLine.serviceId);
                },
              },
              providerFee: {
                required,
                isValidProviderFee,
              },
              uploadedFiles: {
                isValidFile(uploadedFiles) {
                  if (uploadedFiles?.fileInvalid) return false;
                  if (this.fileRequired) {
                    return (uploadedFiles?.size > 0) || (this.isClaim === false);
                  }
                  return true;
                },
              },
            }),
          },
        },
      };

      return obj;
    },
  },

  validations() {
    return this.validationsObj;
  },

  watch: {
    'feature.step1': {
      handler() {
        // if any of step 1 is changed while this component is not active, reset validation
        if (!this.isActive) {
          this.v$.$reset();
          this.duplicateService = false;
          this.duplicatedCount = 0;
          this.removedCount = 0;
          this.feature.step4.claimLines.forEach((claimLine) => {
            claimLine.serviceDate = this.feature.step1.serviceDate;
          });
          this.formResponse = this.cloneDeep(this.feature.step4);
        }
      },
      deep: true,
    },

    'feature.step4.claimLines': {
      handler(newData) {
        let duplicates = false;
        if (!this.isClaim) {
          const claimLines = newData.map(function (obj) {
            return `serviceId ${obj.serviceId} on ${obj.serviceDate}`;
          });

          duplicates = claimLines.some(function (obj, index) {
            return claimLines.indexOf(obj) !== index;
          });
        }

        this.duplicateService = duplicates;
      },
      deep: true,
    },
  },

  methods: {
    checkSomeObjectPropertiesTrue,

    reset() {
      this.v$.$reset();
      this.duplicateService = false;
      this.duplicatedCount = 0;
      this.removedCount = 0;
    },

    dateErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return "We can't accept that input - please remove letters and symbols";
      }

      if (!this.isClaim && errors.some(e => e.$validator === 'isCurrentOrFutureDate')) {
        return "Please enter today's date or a future date.";
      }

      if (this.isClaim && errors.some(e => e.$validator === 'dateInRange')) {
        return "The date of service must be a past date within two years of today's date.";
      }

      return '';
    },

    uploadedFileObj(index) {
      return {
        ...this.formResponse.claimLines[index].uploadedFiles,
      };
    },

    fileAdded(files, extraData) {
      const thisIndex = parseInt(extraData.index, 10);
      const filesSize = Number.isNaN(extraData.totalFileSize) ? 0 : extraData.totalFileSize || 0;

      this.formResponse.claimLines[thisIndex].uploadedFiles.size = filesSize;
      this.formResponse.claimLines[thisIndex].uploadedFiles.fileImgs = extraData.fileImgs;
      this.formResponse.claimLines[thisIndex].uploadedFiles.files = files;
      this.formResponse.claimLines[thisIndex].uploadedFiles.fileInvalid = extraData.fileInvalid;
      if (this.checkFilesUploaded()) {
        this.updateFileUpload(true);
        this.updateFileRemoved(false);
      } else {
        this.updateFileUpload(false);
      }
    },

    checkFilesUploaded() {
      // checks if files are uploaded within any claim line
      let fileFound = false;
      for (let i = 0; i < this.formResponse.claimLines.length; i++) {
        if (this.formResponse.claimLines[i].uploadedFiles.files.length > 0) {
          fileFound = true;
        }
      }
      return fileFound;
    },

    thisClaimLineIsDuplicate(index) {
      const thisClaimLine = this.feature.step4.claimLines[index];

      const duplicateClaimLineIndex = [];
      this.feature.step4.claimLines.forEach((claimLine, idx) => {
        if (claimLine.serviceDate === thisClaimLine.serviceDate && claimLine.serviceId === thisClaimLine.serviceId && Number(index) !== idx) {
          duplicateClaimLineIndex.push(idx);
        }
      });

      return (
        duplicateClaimLineIndex.length > 0 &&
        duplicateClaimLineIndex.every((idx) => {
          return idx < index;
        })
      );
    },

    validToothNumber(toothId, serviceId) {
      // find service with given service Id
      const thisService = this.refData.services.find((obj) => {
        return obj.serviceId === serviceId;
      });

      // if this service requires tooth id, check if given tooth id is valid
      if (thisService !== undefined && toothId !== undefined && this.refData.content.toothNumberOptions) {
        if (this.isToothIdRequired(serviceId)) {
          const id = Number(toothId);
          // toothId must be in between 11 and 85 and length to be 2
          if (id >= 11 && id <= 85 && toothId.length === 2) {
            const bool = this.refData.content.toothNumberOptions
              .map((obj) => {
                // return `returnValue` or `returnData`. `returnData` is for CRM support
                return obj.returnValue || obj.returnData;
              })
              .includes(toothId);

            return bool;
          }

          return false;
        }
      }

      // always return `true` if tooth id is not required to pass form validation
      return true;
    },

    selectService(data, index) {
      const thisIndex = parseInt(index, 10);
      this.formResponse.claimLines[thisIndex].serviceId = data[0]?.returnValue ?? '';
      this.formResponse.claimLines[thisIndex].flexCode = data[0]?.flexCode ?? '';
      this.formResponse.claimLines[thisIndex].toothId = data[0]?.toothId ?? '';
    },

    /**
     * MultiSelect SFC always need to have arr as selectedItems prop
     * return selected option for this multi select.
     */
    selectedService(index) {
      const thisSelectedService = this.formResponse.claimLines[index].serviceId;
      const selectedServiceObj = this.serviceOptions.find((obj) => {
        return obj.returnValue === thisSelectedService;
      });
      const returnArr = [];

      if (selectedServiceObj !== undefined) {
        returnArr.push(selectedServiceObj);
      }

      return returnArr;
    },

    /**
     * Removes the service object by index provided
     */
    removeService(serviceIndex) {
      if (this.formResponse.claimLines !== undefined && this.formResponse.claimLines.length > 1) {
        this.formResponse.claimLines.splice(serviceIndex, 1);
      }
      this.removedCount -= 1;
      if (this.checkFilesUploaded()) {
        this.updateFileUpload(true);
      } else {
        this.updateFileUpload(false);
      }
    },

    /**
     * Adds a new empty service object to the list of services.
     */
    addAnotherService() {
      if (this.formResponse.claimLines !== undefined && this.formResponse.claimLines.length < this.MAX_CLAIMABLE_SERVICE_LINES) {
        this.formResponse.claimLines.push({
          serviceDate: this.feature.step1.serviceDate,
          serviceId: '',
          flexCode: '',
          toothId: '',
          providerFee: '',
          uploadedFiles: this.cloneDeep(UPLOADED_FILES_INITIAL_OBJECT),
        });
      }
    },

    /**
     * Duplicate claim line.
     */
    duplicateThisService(thisClaimLine, index) {
      const claimLineCopy = this.cloneDeep(thisClaimLine);
      claimLineCopy.uploadedFiles = this.cloneDeep(UPLOADED_FILES_INITIAL_OBJECT);
      this.duplicatedCount += 1;
      this.formResponse.claimLines.splice(Number(index) + 1, 0, claimLineCopy);
    },
  },
};
</script>
<style lang="scss" scoped>
:deep(.non-button-style) {
  color: $CRIMSON !important;
}

:deep(.selected-item-tag) {
  color: $DEEP_GREEN !important;
}

:deep(.prefixed-input) {
  @include flex-row;
  @include flex-v-center;
  background-color: $PRIMARY_GREY_20;
  line-height: 3;
  label {
    width: auto;
    height: 100%;
    margin-right: 12px;
    margin-left: 12px;
    text-align: center;
  }
  input {
    margin-bottom: 0;
  }
}

:deep(.wrapper-with-prefix) {
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: 1.5px solid $PRIMARY_GREY_40;
  background-color: $PRIMARY_GREY_20;
  input {
    border: none !important;

    &:focus {
      margin-bottom: unset !important;
    }
  }
}
</style>
